import { Button } from '@/components/Button';
import Input from '../../../../components/Input';
import MultiSelect from '@/components/MultiSelect';
import useFetchActiveUsers from '../../api/useFetchActiveUsers';
import { MdAutorenew, MdChevronRight, MdDragIndicator, MdMoreVert } from 'react-icons/md';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import CandidatesAddDropdown from './CandidatesAddDropdown';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import usePostCandidateBoard from '../api/usePostCandidateBoard';
import usePostCandidateBoardsMoveBoard from '../api/usePostCandidateBoardsMoveBoard';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import clsx from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import type {
  AgentApiCandidateProfilesBoard,
  AgentApiCandidateBoardsIndex,
} from '@/types/serializers';
import { Switch } from '@/components/ui/switch';
import { BOARD_CARD_FIELD_ITEMS } from '../constants';
import useBoardCardVisibleFields from '../hooks/useBoardCardVisibleFields';

function SortableBoardItem({
  board,
  index,
}: {
  board: AgentApiCandidateProfilesBoard;
  index: number;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: board.id,
    data: {
      type: 'board',
      board,
      index,
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    zIndex: isDragging ? 999 : undefined,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#fff' : undefined,
    cursor: 'grab',
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx('flex h-xxl items-center gap-1 px-3', isDragging && 'bg-gray-50')}
      {...attributes}
      {...listeners}
    >
      <div className="cursor-move">
        <MdDragIndicator className="size-icon-xs" />
      </div>
      <span
        className={clsx(
          'flex h-lg items-center justify-center rounded-full px-3 text-xs font-bold',
          board.color_type === 'gray' && 'bg-[#EAEAEA]',
          board.color_type === 'green' && 'bg-[#D7F4E0]',
          board.color_type === 'blue' && 'bg-[#D6EEFF] text-[#0E295D]',
          board.color_type === 'yellow_green' && 'bg-[#FAFFE6]',
          board.color_type === 'yellow' && 'bg-[#FFFEE7]',
          board.color_type === 'orange' && 'bg-[#FFEED3] text-[#461304]',
          board.color_type === 'red' && 'bg-[#FDD] text-[#500000]',
          board.color_type === 'pink' && 'bg-[#FEE4FF] text-[#5C0059]',
          board.color_type === 'purple' && 'bg-[#EEE9FE] text-[#320F66]'
        )}
      >
        {board.title}
      </span>
    </div>
  );
}

// 「未設定」を表す担当者ID。BE の apply_organization_user_condition が -1 を「担当者未設定」として扱う。
const UNASSIGNED_USER_ID = -1;

type Props = {
  screenType: 'list' | 'board';
  setScreenType: (value: 'list' | 'board') => void;
  keyword: string;
  organizationUserIds: number[];
  handleKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  handleOrganizationUserIds: (ids: number[]) => void;
  onBoardAdded?: () => void;
  boards?: AgentApiCandidateBoardsIndex;
  boardsRefetch: () => Promise<unknown>;
};

export default function CandidatesBoardSearch({
  screenType,
  setScreenType,
  keyword,
  organizationUserIds,
  handleKeyword,
  handleOrganizationUserIds,
  onBoardAdded,
  boards,
  boardsRefetch,
}: Props) {
  const { data: activeUsersData } = useFetchActiveUsers();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isFieldMenuOpen, setIsFieldMenuOpen] = useState(false);
  const { visibleFields, setFieldVisibility } = useBoardCardVisibleFields();
  const { mutateAsync: addBoard } = usePostCandidateBoard();
  const { mutateAsync: moveBoard } = usePostCandidateBoardsMoveBoard();
  const [boardItems, setBoardItems] = useState<AgentApiCandidateProfilesBoard[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (boards?.records) {
      setBoardItems(boards.records.filter((record) => record.divide_status !== 'finished'));
    }
  }, [boards]);

  // 先頭に「未設定」（担当者未設定の候補者）を追加する。全解除は選択なし（空配列）で表現する。
  const organizationUserOptions = [
    { id: UNASSIGNED_USER_ID, name: '未設定' },
    ...(activeUsersData
      ? activeUsersData.records.map((a) => ({
          id: a.id,
          name: a.name,
        }))
      : []),
  ];

  const handleCreateBoard = async () => {
    try {
      await addBoard({
        title: 'Newボード',
        divide_status: '',
        color_type: 'gray',
        order_number: boards ? boards?.records.length + 1 : 1,
      });
      setIsMoreMenuOpen(false);

      if (onBoardAdded) {
        onBoardAdded();
      }
    } catch (error) {
      showErrorToast('ボードの作成に失敗しました');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = boardItems.findIndex((board) => board.id === Number(active.id));
      const newIndex = boardItems.findIndex((board) => board.id === Number(over.id));

      if (oldIndex !== -1 && newIndex !== -1) {
        const newBoardItems = arrayMove(boardItems, oldIndex, newIndex);
        setBoardItems(newBoardItems);

        try {
          await moveBoard({
            targetBoardId: Number(active.id),
            order_number: Number(newIndex),
          });

          await boardsRefetch();
        } catch (error) {
          showErrorToast('ボードの並べ替えに失敗しました');
          setBoardItems(boards?.records || []);
        }
      }
    }
  };

  return (
    <div className="flex items-end gap-4 px-6 pt-3">
      <div className="flex w-[522px] flex-col gap-2">
        <label htmlFor="keyword" className="text-sm font-bold">
          フリーワード検索
        </label>
        <Input
          id="keyword"
          search
          className="w-full"
          autoComplete="off"
          placeholder="フリーワードで絞り込む"
          defaultValue={keyword}
          onChange={handleKeyword}
        />
      </div>
      <div className="flex w-[196px] shrink-0 flex-col gap-2">
        <label className="text-sm font-bold">担当者</label>
        <MultiSelect
          className="w-full"
          options={organizationUserOptions}
          value={organizationUserIds}
          onChange={handleOrganizationUserIds}
          placeholder="担当者を選択"
        />
      </div>
      <div className="ml-auto flex gap-2">
        <CandidatesAddDropdown />
        <Button
          type="button"
          color="secondary"
          size="md"
          onClick={() => setScreenType(screenType === 'list' ? 'board' : 'list')}
        >
          <MdAutorenew className="size-icon-xs" />
          リストビュー
        </Button>
        <Popover open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
          <PopoverTrigger asChild>
            <Button color="secondary" size="md" asChild iconOnly className="">
              <div className="size-[42px]">
                <MdMoreVert className="size-icon-xs" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[178px] rounded-sm bg-white p-0">
            <div className="flex flex-col">
              <button
                key="add-board"
                type="button"
                className="flex h-xxl items-center px-3 text-sm hover:bg-gray_darken"
                onClick={handleCreateBoard}
              >
                新規ボード追加
              </button>
              <Popover open={isSortMenuOpen} onOpenChange={setIsSortMenuOpen}>
                <PopoverTrigger asChild>
                  <button
                    key="sort-board"
                    type="button"
                    className="flex h-xxl items-center px-3 text-sm hover:bg-gray_darken"
                  >
                    ボードの並べ替え
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="left"
                  className="w-[160px] rounded-sm bg-white p-0"
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="flex flex-col">
                      <SortableContext
                        items={boardItems.map((board) => board.id)}
                        strategy={verticalListSortingStrategy}
                        key={boardItems.map((b) => b.id).join(',')}
                      >
                        {boardItems.map((board, index) => (
                          <SortableBoardItem key={board.id} board={board} index={index} />
                        ))}
                      </SortableContext>
                    </div>
                  </DndContext>
                </PopoverContent>
              </Popover>
              <Popover open={isFieldMenuOpen} onOpenChange={setIsFieldMenuOpen}>
                <PopoverTrigger asChild>
                  <button
                    key="visible-fields"
                    type="button"
                    className="flex h-xxl items-center gap-2 px-3 text-sm hover:bg-gray_darken"
                  >
                    項目の表示を編集
                    <MdChevronRight className="ml-auto size-icon-xxs" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="left"
                  className="w-[186px] rounded-sm bg-white p-0"
                >
                  <div className="flex flex-col">
                    {BOARD_CARD_FIELD_ITEMS.map((field) => (
                      <label
                        key={field.key}
                        className="flex h-xxl cursor-pointer items-center gap-2 px-3 text-sm hover:bg-gray_darken"
                      >
                        <Switch
                          checked={visibleFields[field.key]}
                          onCheckedChange={(checked) => setFieldVisibility(field.key, checked)}
                        />
                        {field.label}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
